import { Box, Chip, FormHelperText, type SxProps, type Theme, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { type FC, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import SystemTimePicker from "./SystemTimePicker";

/** A start/end pair in float hours on a 24h clock: 6.5 = 06:30. Midnight is 0, never 24. */
export type HourRange = [start: number, end: number];

/**
 * DST-free anchor for the pickers: only the hour/minute matter, and building a DateTime on a real
 * transition day can land on a local time that doesn't exist. January 1 is never a transition.
 */
const CLOCK_ANCHOR = { year: 2000, month: 1, day: 1 } as const;

/** Single-consumer geometry, not a token: two pickers plus wrapped chips have to fit a table cell. */
const PICKER_WIDTH = 116;

const toDateTime = (hours: number): DateTime =>
	DateTime.fromObject({ ...CLOCK_ANCHOR, hour: Math.trunc(hours), minute: Math.round((hours - Math.trunc(hours)) * 60) });

const toFloatHours = (value: DateTime): number => value.hour + value.minute / 60;

const rangeKey = ([start, end]: HourRange): string => `${start}-${end}`;

/**
 * The app's one clock-range label: `06:00–18:00`, in 24h time.
 *
 * Exported because the same pair of float hours is *edited* here and *read* elsewhere (the Jobs grid's
 * read-only Full Shift Times column), and a second formatter is how "07:00-19:00" and "7:00 – 19:00"
 * end up on two surfaces describing one value. The separator is an EN DASH, matching the one this
 * field already draws between its two pickers.
 */
export const formatHourRange = (range: HourRange): string => `${toDateTime(range[0]).toFormat("HH:mm")}–${toDateTime(range[1]).toFormat("HH:mm")}`;

/**
 * Edit a list of clock-time ranges — configured ranges as removable chips, plus a From/To pair of
 * `SystemTimePicker`s that appends the next one.
 *
 * Built for shift-time configuration, where free text was the failure mode: a typed "7-19" or an
 * uncommitted entry looked saved and wasn't (DAT-728). Constrained pickers can only produce a valid
 * pair, so the only question left is *when* a pair counts as final.
 *
 * The answer is ACCEPT, never change. `onChange` fires on every section the user fills, and the clock
 * popper backfills the sections not chosen yet from its reference date — so a single click on the hour
 * column already emits a complete, valid time in whatever half-day *now* happens to be. Committing
 * there consumed the entry and blanked both fields before the user could reach the minutes, let alone
 * PM: asking for 07:00-19:30 appended 07:00-07:30 instead. `onAccept` is the one signal every entry
 * path shares for "this is my answer" — the popper's OK, the last section of a typed time completing,
 * or the mobile modal's OK — so both pickers commit there and `onChange` only holds what's half-entered.
 * Note that the clock columns themselves never accept (MUI's `closeOnSelect` is off by default, which
 * is what makes the OK button appear); that is deliberate here, since the columns can be clicked in any
 * order and an implicit accept on the meridiem column would resurrect this same bug from the other end.
 *
 * Whichever picker accepts second closes the pair, so From and To can be filled in either order.
 *
 * The pickers are keyed on a nonce for the same reason: MUI suppresses `onAccept` when the accepted
 * value matches that picker's own last accepted one, and clearing our `value` prop deliberately does
 * not reset that memory. Without the remount, reusing an end time across two ranges (07:00-19:00 then
 * 11:00-19:00) would never fire accept again and the second range would silently never land.
 *
 * Values are float hours because that is how the app stores shift times; callers holding "HH:mm"
 * strings convert at the boundary.
 */
export const TimeRangeListField: FC<{
	value: HourRange[];
	onChange: (next: HourRange[]) => void;
	disabled?: boolean;
	/** Overrides for the picker labels; default to the generic From/To. */
	startLabel?: string;
	endLabel?: string;
	/** Forwarded to both pickers — the Settings tables pass their dense borderless inline-editor sx. */
	fieldSx?: SxProps<Theme>;
	sx?: SxProps<Theme>;
	/** Stamped on the wrapper; the pickers derive `-start` / `-end` from it. */
	dataTestId?: string;
	/** 12h AM/PM vs 24h format — forwarded to both pickers. */
	ampm?: boolean;
	/** Render AM/PM as a segmented toggle — forwarded to both pickers. */
	meridiemToggle?: boolean;
}> = ({ value, onChange, disabled = false, startLabel, endLabel, fieldSx, sx, dataTestId, ampm, meridiemToggle }) => {
	const { t } = useTranslation();
	const [start, setStart] = useState<DateTime | null>(null);
	const [end, setEnd] = useState<DateTime | null>(null);
	const [isZeroLength, setIsZeroLength] = useState(false);
	const [pickerNonce, setPickerNonce] = useState(0);

	/**
	 * `onChange` only holds the half-entered time in the field — the append happens in `commit`. The
	 * zero-length verdict is stored rather than derived because it is about a COMPLETED pair: derived,
	 * it would flash while the user is still picking (start 07:00, end still on its hour column at
	 * 07:00), which is the mid-entry noise this control is meant to remove.
	 */
	const holdStart = useCallback((next: DateTime | null) => {
		setStart(next);
		setIsZeroLength(false);
	}, []);

	const holdEnd = useCallback((next: DateTime | null) => {
		setEnd(next);
		setIsZeroLength(false);
	}, []);

	const commit = useCallback(
		(nextStart: DateTime | null, nextEnd: DateTime | null) => {
			// The other half of the pair isn't filled (or is still a partial typed time) — nothing to
			// judge yet, so leave both fields as they are and wait for the second accept.
			if (!nextStart?.isValid || !nextEnd?.isValid) {
				setIsZeroLength(false);
				return;
			}

			const range: HourRange = [toFloatHours(nextStart), toFloatHours(nextEnd)];
			if (range[0] === range[1]) {
				setIsZeroLength(true);
				return;
			}

			setIsZeroLength(false);
			// Clear both fields for the next range. A duplicate needs no feedback — the chip the user is
			// asking for is already on screen — so it resets without appending a second copy.
			setStart(null);
			setEnd(null);
			// Both branches blank the fields, so both must remount the pickers to clear MUI's memory of
			// the value it last accepted; otherwise re-entering that same time emits no `onAccept`.
			setPickerNonce((previous) => previous + 1);
			if (!value.some((existing) => rangeKey(existing) === rangeKey(range))) onChange([...value, range]);
		},
		[value, onChange],
	);

	const removeRange = useCallback((range: HourRange) => onChange(value.filter((existing) => rangeKey(existing) !== rangeKey(range))), [value, onChange]);

	return (
		<Box data-testid={dataTestId} sx={sx}>
			<Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
				{value.map((range) => (
					<Chip
						key={rangeKey(range)}
						size="small"
						variant="outlined"
						label={formatHourRange(range)}
						onDelete={disabled ? undefined : () => removeRange(range)}
						data-testid={dataTestId && `${dataTestId}-chip-${rangeKey(range)}`}
					/>
				))}
				{/* The pair wraps as one unit: split across two lines, "From" and "To" stop reading as a range. */}
				<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
					<SystemTimePicker
						key={`start-${pickerNonce}`}
						value={start}
						onChange={holdStart}
						onAccept={(next) => commit(next, end)}
						label={startLabel ?? t("time.range.list.start")}
						disabled={disabled}
						// Every mount but the first follows a committed range, so the cursor lands back on From
						// for the next one. The first mount must not autofocus — the consumer renders one of
						// these per job row, and the last row would win the focus and scroll the page to it.
						autoFocus={pickerNonce > 0}
						fullWidth={false}
						ampm={ampm}
						meridiemToggle={meridiemToggle}
						sx={[{ width: PICKER_WIDTH }, ...(Array.isArray(fieldSx) ? fieldSx : [fieldSx])]}
						dataTestId={dataTestId && `${dataTestId}-start`}
					/>
					{/* Borderless fields have no box to group them, so the dash is what makes two adjacent
					    times read as one range. Flex order flips with the document direction in RTL. */}
					<Typography component="span" aria-hidden sx={{ color: "text.secondary" }}>
						–
					</Typography>
					<SystemTimePicker
						key={`end-${pickerNonce}`}
						value={end}
						onChange={holdEnd}
						onAccept={(next) => commit(start, next)}
						label={endLabel ?? t("time.range.list.end")}
						disabled={disabled}
						fullWidth={false}
						ampm={ampm}
						meridiemToggle={meridiemToggle}
						sx={[{ width: PICKER_WIDTH }, ...(Array.isArray(fieldSx) ? fieldSx : [fieldSx])]}
						dataTestId={dataTestId && `${dataTestId}-end`}
					/>
				</Box>
			</Box>
			{isZeroLength && <FormHelperText error>{t("time.range.list.sameStartAndEnd")}</FormHelperText>}
		</Box>
	);
};

export default TimeRangeListField;
